import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Dialog, FontIcon, Toggle } from 'material-ui';
import { connect } from 'react-redux';
import { get, find, size, sumBy, last, orderBy } from 'lodash';
import moment from 'moment';
import KTitle from '../../../components/KJRN/KTitle';
import DayEntryForm from './DayEntryForm';
import TimeSpanEntryForm from './TimeSpanEntryForm';

const EntryModal = ({
  modalState, closeModal, entryTypes, selectedEntries, workingHours, deselectAll, valuesByKey, entriesByDate, holidays,
  initialDate, isEdit, dispatch, start, end, timeEmailTypes,
}) => {
  const [defaultValues, setDefaultValues] = useState({});
  const [toggleValue, setToggleValue] = useState(false);
  const [toggleValueNotAvaliable, setToggleValueNotAvaliable] = useState(false);
  const [primaryButton, setPrimaryButton] = useState('wholeDay');
  const workingHour = find(workingHours, h => moment().isSameOrAfter(get(h, 'Begda')) && moment().isSameOrBefore(get(h, 'Endda')));
  const hrsPerDay = get(workingHour, 'HrsPrDay', 0).toFixed(2);

  const whichButton = (hours) => {
    if (hrsPerDay <= 0.00) {
      setPrimaryButton('');
    } else if (hours === hrsPerDay) {
      setPrimaryButton('wholeDay');
    } else if (hours === ((hrsPerDay / 2).toFixed(2))) {
      setPrimaryButton('halfDay');
    } else {
      setPrimaryButton('');
    }
  };

  const emptyEntry = {
    clockCardEntryTypeID: null,
    clockCardEntryComment: '',
    actualClockTime: 0,
    clockTimeHasError: false,
  };
  const getRemainingHours = (date) => {
    const currentDate = date ? moment(date).format('DD/MM/YY') : moment().format('DD/MM/YY');
    const employeeEntries = get(entriesByDate, currentDate);
    if (employeeEntries && employeeEntries.length > 0) {
      const total = sumBy(employeeEntries, 'ActualClockTime');
      const expected = employeeEntries[0].ExpectedClockTime;
      const remaining = (expected - total).toFixed(2);
      return remaining < 0 ? 0 : parseFloat(remaining);
    }
    return hrsPerDay;
  };

  const setDefault = () => {
    let clockIn = initialDate || moment();
    let clockOut = initialDate || moment();
    const remaining = size(selectedEntries) === 0 ? getRemainingHours(initialDate) : hrsPerDay;
    const employeeEntries = get(entriesByDate, clockIn.format('DD/MM/YY'));
    if (employeeEntries && employeeEntries.length > 0) {
      const lastEntry = get(last(orderBy(employeeEntries, 'ClockOut')), 'ClockOut');
      clockIn = moment(lastEntry);
    } else {
      clockIn = clockIn.set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
    }
    clockOut = moment(clockIn).add(remaining, 'hours');
    setToggleValueNotAvaliable(false);
    setDefaultValues({
      ...emptyEntry,
      // eslint-disable-next-line
      date: initialDate ? initialDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      timeIn: clockIn.format('YYYY-MM-DD HH:mm:ss'),
      timeOut: clockOut.format('YYYY-MM-DD HH:mm:ss'),
      registerWeekends: false,
      registerRedDays: false,
      dateFrom: '',
      dateTo: '',
      hours: parseFloat(remaining),
    });
    whichButton(hrsPerDay);
  };
  useEffect(() => {
    if (size(selectedEntries) !== 0) {
      const entry = selectedEntries[0];
      setToggleValueNotAvaliable(true);
      const clockIn = moment(get(entry, 'ClockIn'));
      const clockOut = moment(get(entry, 'ClockOut'));
      const duration = moment.duration(moment(clockOut).diff(moment(clockIn)));
      const hours = duration.asHours().toFixed(2);
      whichButton(hours);
      setDefaultValues({
        date: moment(clockIn).format('YYYY-MM-DD'),
        timeIn: clockIn.format('YYYY-MM-DD HH:mm:ss'),
        timeOut: clockOut.format('YYYY-MM-DD HH:mm:ss'),
        clockCardEntryTypeID: entry.ClockCardEntryTypeID,
        clockCardEntryComment: entry.ClockCardEntryComment,
        actualClockTime: entry.ActualClockTime,
        clockTimeHasError: false,
        hours,
      });
    } else {
      setDefault();
    }
  }, [selectedEntries, initialDate]);

  useEffect(() => {
    setDefault();
  }, [toggleValue]);

  const clearAndClose = () => {
    setDefaultValues(defaultValues);
    if (defaultValues.hours) {
      whichButton(defaultValues.hours);
    }

    closeModal();
    setToggleValueNotAvaliable(false);
    setToggleValue(false);
    if (deselectAll) {
      deselectAll();
    }
  };

  return (
    <Dialog
      contentStyle={{
        overflow: 'hidden',
        maxWidth: 500,
        width: '100%',
      }}
      bodyStyle={{
        overflowY: 'auto',
      }}
      modal
      open={modalState}
      onRequestClose={clearAndClose}
    >
      <div>
        <KTitle
          style={{
            marginLeft: 0,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
          }}
          title={(
            <React.Fragment>
              <FontIcon
                style={{
                  marginRight: 6, fontSize: 28, color: 'inherit',
                }}
                className="material-icons"
              >alarm_on
              </FontIcon>
              <FormattedMessage id="time.CreateEntry" />
            </React.Fragment>
          )}
          subtitleStyle={{ margin: 0, fontSize: 12, lineHeight: '1' }}
        />
        {!toggleValueNotAvaliable && (<Toggle
          label={<FormattedMessage id="time.span" />}
          labelPosition="right"
          defaultToggled={toggleValue}
          onToggle={() => { setToggleValue(!toggleValue); }}
          style={{ marginTop: 20, width: 50 }}
        />)}

        {!toggleValue ? (
          <DayEntryForm
            defaultValues={defaultValues}
            selectedEntries={selectedEntries}
            hrsPerDay={hrsPerDay}
            entryTypes={entryTypes}
            primaryButton={primaryButton}
            setPrimaryButton={setPrimaryButton}
            clearAndClose={clearAndClose}
            whichButton={whichButton}
            valuesByKey={valuesByKey}
            entriesByDate={entriesByDate}
            getRemainingHours={getRemainingHours}
            holidays={holidays}
            isEdit={isEdit}
            dispatch={dispatch}
            start={start}
            end={end}
            timeEmailTypes={timeEmailTypes}
          />
        ) : (
          <TimeSpanEntryForm
            defaultValues={defaultValues}
            selectedEntries={selectedEntries}
            entryTypes={entryTypes}
            clearAndClose={clearAndClose}
            holidays={holidays}
            dispatch={dispatch}
            start={start}
            end={end}
            timeEmailTypes={timeEmailTypes}
          />)}
      </div>
    </Dialog>
  );
};

EntryModal.propTypes = {
  modalState: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  entryTypes: PropTypes.shape({}).isRequired,
  workingHours: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedEntries: PropTypes.arrayOf(PropTypes.shape({
    ClockCardEntryID: PropTypes.number,
    ClockIn: PropTypes.string,
    ClockOut: PropTypes.string,
    ActualClockTime: PropTypes.number,
    ClockTimeHasError: PropTypes.bool,
    ClockCardEntryTypeID: PropTypes.number,
    ClockCardEntryComment: PropTypes.string,
  })),
  deselectAll: PropTypes.func,
  valuesByKey: PropTypes.object,
  entriesByDate: PropTypes.shape({}).isRequired,
  holidays: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialDate: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  isEdit: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  start: PropTypes.object.isRequired,
  end: PropTypes.object.isRequired,
  timeEmailTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default connect(
  ({ timeReducer, WebValuesReducer, EmployeeHolidayReducer }) => ({
    entryTypes: timeReducer.entryTypes,
    workingHours: timeReducer.workingHours,
    valuesByKey: WebValuesReducer.valuesByKey,
    holidays: EmployeeHolidayReducer.holidays,
    start: timeReducer.start,
    end: timeReducer.end,
    timeEmailTypes: timeReducer.timeEmailTypes,
  }),

  dispatch => ({ dispatch }),
)(EntryModal);



// WEBPACK FOOTER //
// ./src/sites/time/components/EntryModal.js